import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { workspaceWatchlistsQueryKeys } from '@/constants';
import { tagsService, watchlistsService } from '@/services';
import { useUserStore, useWatchlistStore } from '@/stores';
import { SubjectReference, SubjectWatchlist, TagInput, WatchlistInput, WorkspaceSubjectType } from '@/types';

const TAGS_QUERY_KEY = ['workspace-tags'] as const;

export const useTags = () =>
    useQuery({
        queryKey: TAGS_QUERY_KEY,
        queryFn: tagsService.list,
        staleTime: 60_000,
    });

export const useSubjectTags = (subject?: Pick<SubjectReference, 'subjectId' | 'subjectType'>) => {
    const subjectQuery = subject ? { subjectId: subject.subjectId, subjectType: subject.subjectType } : undefined;

    return useQuery({
        queryKey: [...TAGS_QUERY_KEY, 'subject', subjectQuery],
        queryFn: () => tagsService.listForSubject(subjectQuery!),
        enabled: Boolean(subjectQuery?.subjectId),
        staleTime: 30_000,
    });
};

export const useSubjectsTags = (subjects: Array<Pick<SubjectReference, 'subjectId' | 'subjectType'>>) => {
    const normalizedSubjects = Array.from(
        new Map(
            subjects
                .filter((subject) => Boolean(subject.subjectId))
                .map(({ subjectId, subjectType }) => [`${subjectType}:${subjectId}`, { subjectId, subjectType }]),
        ).values(),
    ).sort((left, right) =>
        `${left.subjectType}:${left.subjectId}`.localeCompare(`${right.subjectType}:${right.subjectId}`),
    );

    return useQuery({
        queryKey: [...TAGS_QUERY_KEY, 'subjects', normalizedSubjects],
        queryFn: () => tagsService.listForSubjects(normalizedSubjects),
        enabled: normalizedSubjects.length > 0,
        staleTime: 30_000,
    });
};

export const useTagSubjects = (tagIds: string[], subjectType: WorkspaceSubjectType) => {
    const normalizedTagIds = Array.from(new Set(tagIds)).sort();

    return useQuery({
        queryKey: [...TAGS_QUERY_KEY, 'tag-subjects', subjectType, normalizedTagIds],
        queryFn: () => tagsService.listSubjects(normalizedTagIds, subjectType),
        enabled: normalizedTagIds.length > 0,
        staleTime: 30_000,
    });
};

export const useTagSubjectsForTypes = (
    tagIds: string[],
    subjectTypes: readonly WorkspaceSubjectType[],
    matchAll = false,
) => {
    const normalizedTagIds = Array.from(new Set(tagIds)).sort();
    const normalizedSubjectTypes = Array.from(new Set(subjectTypes)).sort();

    return useQuery({
        queryKey: [...TAGS_QUERY_KEY, 'tag-subjects', normalizedSubjectTypes, normalizedTagIds, { matchAll }],
        queryFn: () => tagsService.listSubjects(normalizedTagIds, normalizedSubjectTypes, matchAll),
        enabled: normalizedTagIds.length > 0 && normalizedSubjectTypes.length > 0,
        staleTime: 30_000,
    });
};

export const useWatchlists = () => {
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    const hydrateWatchlists = useWatchlistStore((state) => state.hydrateWatchlists);
    const query = useQuery({
        queryKey: workspaceWatchlistsQueryKeys.list(activeWorkspaceId),
        queryFn: watchlistsService.list,
        staleTime: 60_000,
    });

    useEffect(() => {
        if (query.data) {
            hydrateWatchlists(query.data, activeWorkspaceId);
        }
    }, [activeWorkspaceId, hydrateWatchlists, query.data]);

    return query;
};

export const useSubjectWatchlists = (subject?: Pick<SubjectReference, 'subjectId' | 'subjectType'>) => {
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    const subjectQuery = subject ? { subjectId: subject.subjectId, subjectType: subject.subjectType } : undefined;

    return useQuery({
        queryKey: [...workspaceWatchlistsQueryKeys.subject(activeWorkspaceId), subjectQuery],
        queryFn: () => watchlistsService.listForSubject(subjectQuery!),
        enabled: Boolean(subjectQuery?.subjectId),
        staleTime: 30_000,
    });
};

export const useWatchlistSubjects = (watchlistIds: string[], subjectType: WorkspaceSubjectType) => {
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    const normalizedWatchlistIds = Array.from(new Set(watchlistIds)).sort();

    return useQuery({
        queryKey: [
            ...workspaceWatchlistsQueryKeys.watchlistSubjects(activeWorkspaceId),
            subjectType,
            normalizedWatchlistIds,
        ],
        queryFn: () => watchlistsService.listSubjects(normalizedWatchlistIds, subjectType),
        enabled: normalizedWatchlistIds.length > 0,
        staleTime: 30_000,
    });
};

const useInvalidateTags = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: TAGS_QUERY_KEY });
};

export const useCreateTag = () => {
    const invalidateTags = useInvalidateTags();

    return useMutation({ mutationFn: (input: TagInput) => tagsService.create(input), onSuccess: invalidateTags });
};

export const useUpdateTag = () => {
    const invalidateTags = useInvalidateTags();

    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: Partial<TagInput> }) => tagsService.update(id, input),
        onSuccess: invalidateTags,
    });
};

export const useDeleteTag = () => {
    const invalidateTags = useInvalidateTags();

    return useMutation({ mutationFn: tagsService.delete, onSuccess: invalidateTags });
};

export const useToggleTagSubject = (subject: SubjectReference) => {
    const invalidateTags = useInvalidateTags();

    return useMutation({
        mutationFn: async ({
            assignmentId,
            selected,
            tagId,
        }: {
            assignmentId?: string;
            selected: boolean;
            tagId: string;
        }) => {
            if (selected && assignmentId) {
                await tagsService.removeAssignment(tagId, assignmentId);
            } else {
                await tagsService.addAssignment(tagId, subject);
            }
        },
        onSuccess: invalidateTags,
    });
};

export const useCreateWatchlist = () => {
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);

    return useMutation({
        mutationFn: async (input: WatchlistInput) => {
            const watchlist = await useWatchlistStore.getState().createWatchlist(input, activeWorkspaceId);

            if (!watchlist) {
                throw new Error('The watchlist could not be created.');
            }

            return watchlist;
        },
    });
};

export const useUpdateWatchlist = () => {
    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: Partial<WatchlistInput> }) =>
            useWatchlistStore.getState().updateWatchlist(id, input),
    });
};

export const useDeleteWatchlist = () => {
    return useMutation({
        mutationFn: (watchlistId: string) => useWatchlistStore.getState().deleteWatchlist(watchlistId),
    });
};

export const useToggleWatchlistSubject = (subject: SubjectReference) => {
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    const queryClient = useQueryClient();
    const subjectQueryKey = [
        ...workspaceWatchlistsQueryKeys.subject(activeWorkspaceId),
        { subjectId: subject.subjectId, subjectType: subject.subjectType },
    ] as const;

    return useMutation({
        mutationFn: async ({
            itemId,
            selected,
            watchlistId,
        }: {
            itemId?: string;
            selected: boolean;
            watchlistId: string;
        }) => useWatchlistStore.getState().toggleSubject(watchlistId, subject, selected, itemId),
        onMutate: async ({ itemId, selected, watchlistId }) => {
            await queryClient.cancelQueries({ queryKey: subjectQueryKey });
            const previousWatchlists = queryClient.getQueryData<SubjectWatchlist[]>(subjectQueryKey);

            queryClient.setQueryData<SubjectWatchlist[]>(subjectQueryKey, (watchlists) =>
                watchlists?.map((watchlist) =>
                    watchlist.id === watchlistId
                        ? {
                              ...watchlist,
                              itemId: selected ? undefined : itemId,
                              selected: !selected,
                          }
                        : watchlist,
                ),
            );

            return { previousWatchlists };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousWatchlists) {
                queryClient.setQueryData(subjectQueryKey, context.previousWatchlists);
            }
        },
        onSettled: () => queryClient.invalidateQueries({ exact: true, queryKey: subjectQueryKey }),
    });
};
